import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addTask } from '../features/tasks/tasksSlice';

const TaskInput = () => {
    const dispatch = useDispatch();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState('low');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !dueDate) return;

        setIsSubmitting(true);

        try {
            dispatch(addTask({ title: title.trim(), description, dueDate, priority }));
            setTitle('');
            setDescription('');
            setDueDate('');
            setPriority('low');
            console.log('Task added:', { title, description, dueDate, priority });
        } catch (error) {
            console.error('Error adding task:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const priorityOptions = [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' }
    ];

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-transparent backdrop-blur-xl border border-gray-800/30 rounded-3xl p-8 shadow-2xl transform hover:scale-[1.02] transition-all duration-300">

                <h2 className="text-2xl font-light text-white mb-8 text-center">Create New Task</h2>
                
                <div className="space-y-6">
                    {/* Task Title Input */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Task Title</label>
                        <div className="relative group">
                            <input
                                type="text"
                                placeholder="What needs to be accomplished?"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 group-hover:border-gray-600/50"
                                required
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                        </div>
                    </div>

                    {/* Description Input */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Description</label>
                        <div className="relative group">
                            <textarea
                                placeholder="Provide additional context..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows="3"
                                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 resize-none group-hover:border-gray-600/50"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-blue-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                        </div>
                    </div>

                    {/* Due Date and Priority Row */}
                    <div className="grid grid-cols-1 gap-6">
                        {/* Due Date Input */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-300">Due Date</label>
                            <div className="relative group">
                                <input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 group-hover:border-gray-600/50"
                                    required
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            </div>
                        </div>

                        {/* Priority Level */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-300">Priority Level</label>
                            <div className="flex space-x-2">
                                {priorityOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setPriority(option.value)}
                                        className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                                            priority === option.value
                                                ? option.value === 'low'
                                                    ? 'bg-green-600/20 border-2 border-green-500 text-green-400 shadow-lg shadow-green-500/20'
                                                    : option.value === 'medium'
                                                    ? 'bg-yellow-600/20 border-2 border-yellow-500 text-yellow-400 shadow-lg shadow-yellow-500/20'
                                                    : 'bg-red-600/20 border-2 border-red-500 text-red-400 shadow-lg shadow-red-500/20'
                                                : 'bg-gray-900/30 border border-gray-700/30 text-gray-400 hover:border-gray-600/50 hover:text-gray-300'
                                        }`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            onClick={handleSubmit}
                            disabled={isSubmitting || !title.trim() || !dueDate}
                            className="group relative w-full bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-size-200 text-white font-semibold py-4 px-6 rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none overflow-hidden"
                            style={{
                                backgroundSize: '200% 100%',
                                backgroundPosition: isSubmitting ? '100% 0' : '0% 0'
                            }}
                        >
                            {/* Animated background overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            
                            {/* Button content */}
                            <div className="relative flex items-center justify-center">
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                                        <span>Creating...</span>
                                    </>
                                ) : (
                                    <span>Create Task</span>
                                )}
                            </div>

                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskInput;












